import { create } from "zustand";
import { persist } from "zustand/middleware";
import { IAny } from "@/types/breeze/Any";
import { TENANT_ID_KEY } from "@/lib/auth";
import { IEntity } from "@/types/breeze/Entity";

// Define a more specific type for rawData to avoid 'any'
export type KanbanCardData = {
  id: number;
  title?: string;
  description?: string;
  position?: number;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

// Type definitions for the simplified state structure
export interface KanbanColumn {
  id: string;
  title: string;
  position: number;
  // Map of position to card ID
  sort_order: Record<number, number | string>;
}

export interface StatusFields {
  [statusField: string]: {
    [columnId: string]: KanbanColumn;
  };
}

export interface KanbanCard {
  id: number | string;
  title: string;
  description?: string;
  column_id: string;
  position: number;
  entityName: string;
  created_at?: string;
  updated_at?: string;
  rawData: Record<string, unknown>;
}

export interface EntityData {
  items: Record<string | number, IAny>; // Store items by ID for quick access
  columns: StatusFields; // Status fields -> columns -> sort orders
  currentStatusField?: string; // Current active status field for this entity
}

// Updated to include tenant separation
export interface TenantEntityData {
  [entityName: string]: EntityData;
}

// Updated to have tenants at the top level
export interface KanbanBoard {
  [tenantId: string]: TenantEntityData;
}

// Define proper types for drag and drop operations
export interface CardSource {
  fromPosition: number;
  fromColumnId: string;
}

export interface CardDestination {
  toPosition: number;
  toColumnId: string;
}

// Column types for better type safety
export interface ColumnSource {
  fromPosition: number;
}

export interface ColumnDestination {
  toPosition: number;
}

// Helper to get the current tenant ID from localStorage
const getCurrentTenantId = (): string | null => {
  try {
    if (typeof window !== "undefined") {
      const tenantId = localStorage.getItem(TENANT_ID_KEY);
      return tenantId;
    }
  } catch (error) {
    console.error("Error accessing localStorage for tenant ID:", error);
  }
  return null;
};

interface KanbanStore {
  // State
  board: KanbanBoard;
  currentEntity: string | null;
  currentTenant: string | null;

  // Actions
  setCurrentTenant: (tenantId: string) => void;
  setStatusField: (
    field: string,
    entityName: string,
    tenantId?: string,
  ) => void;
  getStatusField: (entityName: string, tenantId?: string) => string | undefined;
  createBoardFromData: (
    data: IAny[],
    entityDefinition: IEntity,
    tenantId: string,
  ) => void;
  handleCardMove: (
    card: KanbanCard,
    source: CardSource,
    destination: CardDestination,
    tenantId?: string,
  ) => void;
  handleColumnMove: (
    column: KanbanColumn,
    source: ColumnSource,
    destination: ColumnDestination,
    entityName: string,
    tenantId?: string,
  ) => void;
  updateEntityColumns: (
    entityName: string,
    statusField: string,
    tenantId: string,
  ) => void;
  clearBoard: (tenantId?: string) => void;
}

// Create and persist the store
const useKanbanStore = create<KanbanStore>()(
  persist(
    (set, get) => ({
      // Initial state - retrieve tenant ID from localStorage if available
      board: {},
      currentEntity: null,
      currentTenant: getCurrentTenantId(),

      // Set current tenant and also update localStorage
      setCurrentTenant: (tenantId: string) => {
        set({ currentTenant: tenantId });
        // We don't need to update localStorage here as that should be handled by the app's tenant switcher
      },

      // Clear the board
      clearBoard: (tenantId?: string) => {
        const { currentEntity, currentTenant } = get();
        const targetTenant = tenantId || currentTenant;

        if (currentEntity && targetTenant) {
          const newBoard = { ...get().board };

          // If tenant exists, remove just the entity
          if (newBoard[targetTenant]) {
            const tenantData = { ...newBoard[targetTenant] };
            delete tenantData[currentEntity];
            newBoard[targetTenant] = tenantData;
          }

          set({ board: newBoard });
        }
      },

      // Set the status field (now only stored at entity level)
      setStatusField: (
        field: string,
        entityName: string,
        tenantId?: string,
      ) => {
        const { board, currentTenant } = get();
        const targetTenant = tenantId || currentTenant;

        if (!targetTenant) {
          console.error("No tenant ID provided for setting status field");
          return;
        }

        // Ensure tenant and entity data structures exist
        const tenantData = board[targetTenant] || {};
        const entityData = tenantData[entityName] || { items: {}, columns: {} };

        // Update only the entity-specific field
        set({
          board: {
            ...board,
            [targetTenant]: {
              ...tenantData,
              [entityName]: {
                ...entityData,
                currentStatusField: field,
              },
            },
          },
        });

        // Update columns when status field changes
        get().updateEntityColumns(entityName, field, targetTenant);
      },

      // Get stored status field from entity
      getStatusField: (entityName: string, tenantId?: string) => {
        const { board, currentTenant } = get();
        const targetTenant = tenantId || currentTenant;

        if (!targetTenant) {
          console.error("No tenant ID provided for getting status field");
          return undefined;
        }

        // Check if the tenant and entity have a stored status field
        if (board[targetTenant]?.[entityName]?.currentStatusField) {
          return board[targetTenant][entityName].currentStatusField;
        }

        // Return undefined if not found
        return undefined;
      },

      // Update columns for an entity based on the status field
      updateEntityColumns: (
        entityName: string,
        statusField: string,
        tenantId: string,
      ) => {
        const { board } = get();

        if (!tenantId) {
          console.error("No tenant ID provided for updating entity columns");
          return;
        }

        const tenantData = board[tenantId] || {};
        const entityData = tenantData[entityName];

        if (!entityData || !statusField) return;

        try {
          // Get existing columns for this status field
          const existingColumnsForStatusField =
            entityData.columns[statusField] || {};
          const existingColumnIds = Object.keys(existingColumnsForStatusField);

          // Check if we already have columns for this status field
          const hasExistingColumns = existingColumnIds.length > 0;

          // Get unique status values from the data only if we need to create new columns
          const statusValues = [
            ...new Set(
              Object.values(entityData.items).map((item) => item[statusField]),
            ),
          ].filter(Boolean);

          // Skip processing if nothing changed (all values already have columns)
          const allValuesHaveColumns = statusValues.every((value) =>
            existingColumnIds.includes(String(value)),
          );

          // If we have existing columns and all values already have columns, we can skip
          if (
            hasExistingColumns &&
            allValuesHaveColumns &&
            statusValues.length === existingColumnIds.length
          ) {
            return;
          }

          // Update or create new columns
          const newColumns: Record<string, KanbanColumn> = {
            ...existingColumnsForStatusField,
          };

          // Create a column for each unique status value that doesn't exist yet
          statusValues.forEach((statusValue) => {
            const statusId = String(statusValue);

            // Check if column already exists in the current status field
            if (!newColumns[statusId]) {
              // Create new column with empty sort order
              newColumns[statusId] = {
                id: statusId,
                title: String(statusValue),
                position: Object.keys(newColumns).length, // Position based on number of columns
                sort_order: {}, // Empty sort order for new columns
              };
            }
          });

          // Only process items for columns that need sort orders
          const columnsNeedingSortOrders = Object.values(newColumns).filter(
            (column) => Object.keys(column.sort_order).length === 0,
          );

          if (columnsNeedingSortOrders.length > 0) {
            // Group items by column (status value)
            const itemsByColumn: Record<string, IAny[]> = {};

            Object.values(entityData.items).forEach((item) => {
              const itemStatus = item[statusField];

              if (!itemStatus) return;

              const columnId = String(itemStatus);
              // Only collect items for columns that need sort orders
              if (
                newColumns[columnId] &&
                Object.keys(newColumns[columnId].sort_order).length === 0
              ) {
                if (!itemsByColumn[columnId]) {
                  itemsByColumn[columnId] = [];
                }

                itemsByColumn[columnId].push(item);
              }
            });

            // Populate sort orders based on item positions for new columns only
            Object.entries(itemsByColumn).forEach(([columnId, items]) => {
              const column = newColumns[columnId];
              if (!column) return;

              // Sort items by position if available
              const sortedItems = [...items].sort((a, b) => {
                const posA = typeof a.position === "number" ? a.position : 9999;
                const posB = typeof b.position === "number" ? b.position : 9999;
                return posA - posB;
              });

              // Create sort order mapping
              sortedItems.forEach((item, index) => {
                column.sort_order[index] = item.id;
              });
            });
          }

          // Update positions for all columns to ensure they're sequential
          if (hasExistingColumns) {
            // Use existing positions where available
            const columnEntries = Object.entries(newColumns).sort(
              ([, a], [, b]) => a.position - b.position,
            );

            // Update positions to be sequential
            columnEntries.forEach(([id, column], index) => {
              newColumns[id] = {
                ...column,
                position: index,
              };
            });
          }

          // Update entity data with new columns
          set({
            board: {
              ...board,
              [tenantId]: {
                ...tenantData,
                [entityName]: {
                  ...entityData,
                  currentStatusField: statusField, // Ensure status field is saved with entity
                  columns: {
                    ...entityData.columns,
                    [statusField]: newColumns,
                  },
                },
              },
            },
          });
        } catch (error) {
          console.error("Error updating columns:", error);
        }
      },

      // Create a Kanban board from data and columns
      createBoardFromData: (
        data: IAny[],
        entityDefinition: IEntity,
        tenantId: string,
      ) => {
        const { currentEntity } = get();

        if (!tenantId) {
          console.error("No tenant ID provided for creating board data");
          return;
        }

        // Update current entity if changed
        if (currentEntity !== entityDefinition.name) {
          set({ currentEntity: entityDefinition.name });
        }

        if (!data.length) {
          return;
        }

        try {
          // Get existing tenant data
          const tenantData = get().board[tenantId] || {};

          // Get existing entity data if available
          const existingEntityData = tenantData[entityDefinition.name];
          const currentStatusField = existingEntityData?.currentStatusField;

          // Create entity data structure
          const entityData: EntityData = {
            items: {},
            columns: existingEntityData?.columns || {},
            currentStatusField: currentStatusField, // Preserve the current status field
          };

          // Store items by ID
          data.forEach((item) => {
            entityData.items[item.id] = item;
          });

          // Update the board with the new entity data
          set((state) => ({
            board: {
              ...state.board,
              [tenantId]: {
                ...tenantData,
                [entityDefinition.name]: entityData,
              },
            },
          }));

          // Update columns based on current status field if available
          if (currentStatusField) {
            get().updateEntityColumns(
              entityDefinition.name,
              currentStatusField,
              tenantId,
            );
          }
        } catch (error) {
          console.error("Error creating board:", error);
        }
      },

      // Handle card movement between columns or within a column
      handleCardMove: (
        card: KanbanCard,
        source: CardSource,
        destination: CardDestination,
        tenantId?: string,
      ) => {
        const { board, currentEntity, currentTenant } = get();
        const targetTenant = tenantId || currentTenant;

        if (!targetTenant) {
          console.error("No tenant ID provided for card move");
          return;
        }

        if (!currentEntity || !board[targetTenant]?.[currentEntity]) {
          console.error("No board available for card move");
          return;
        }

        const entityData = board[targetTenant][currentEntity];
        const currentStatusField = entityData.currentStatusField;

        if (!currentStatusField) {
          console.error("No status field available for card move");
          return;
        }

        try {
          // Make a deep copy of entity data
          const newEntityData = { ...entityData };

          // Get the current status field columns
          const columnsForStatusField =
            newEntityData.columns[currentStatusField] || {};
          if (Object.keys(columnsForStatusField).length === 0) {
            console.error(
              "No columns found for status field:",
              currentStatusField,
            );
            return;
          }

          // Get source and destination columns
          const sourceColumn = columnsForStatusField[source.fromColumnId];
          const destColumn = columnsForStatusField[destination.toColumnId];

          if (!sourceColumn || !destColumn) {
            console.error("Source or destination column not found", {
              source: source.fromColumnId,
              destination: destination.toColumnId,
            });
            return;
          }

          // Create deep copies of the columns to avoid mutation
          const newColumnsForStatusField = { ...columnsForStatusField };
          const newSourceColumn = {
            ...sourceColumn,
            sort_order: { ...sourceColumn.sort_order },
          };
          const newDestColumn = {
            ...destColumn,
            sort_order: { ...destColumn.sort_order },
          };

          // Get the item ID from the card
          const itemId =
            typeof card.id === "string" ? parseInt(card.id, 10) : card.id;

          // Get the item
          const item = newEntityData.items[itemId];
          if (!item) {
            console.error("Item not found:", itemId);
            return;
          }

          // Create a new version of the item
          const updatedItem = {
            ...item,
            [currentStatusField]: destination.toColumnId,
            position: destination.toPosition,
            updated_at: new Date().toISOString(),
          };

          // Update the items map
          const newItems = { ...newEntityData.items, [itemId]: updatedItem };

          // STEP 1: Remove card from source column sort order
          // Find the position of the card in the source column
          let sourcePosition = -1;
          Object.entries(newSourceColumn.sort_order).forEach(([pos, id]) => {
            if (id.toString() === itemId.toString()) {
              sourcePosition = parseInt(pos, 10);
            }
          });

          if (sourcePosition === -1) {
            console.error("Card not found in source column sort order");
            return;
          }

          // Create a new sort order for the source column without the moved card
          const newSourceSortOrder: Record<number, number | string> = {};
          let positionCounter = 0;

          // Rebuild the sort order, skipping the moved card and reindexing
          Object.entries(newSourceColumn.sort_order)
            .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
            .forEach(([, id]) => {
              if (id.toString() !== itemId.toString()) {
                newSourceSortOrder[positionCounter++] = id;
              }
            });

          newSourceColumn.sort_order = newSourceSortOrder;

          // STEP 2: Add card to destination column sort order
          // If source and destination are the same, use the updated source sort order
          const destSortOrder =
            source.fromColumnId === destination.toColumnId
              ? { ...newSourceSortOrder }
              : { ...newDestColumn.sort_order };

          // Insert the card at the destination position
          const newDestSortOrder: Record<number, number | string> = {};
          let insertedCard = false;
          positionCounter = 0;

          // Handle edge case: if the destination position is higher than the number of cards
          const destSortOrderEntries = Object.entries(destSortOrder).sort(
            ([a], [b]) => parseInt(a, 10) - parseInt(b, 10),
          );

          if (destination.toPosition >= destSortOrderEntries.length) {
            // Add all existing cards
            destSortOrderEntries.forEach(([, id]) => {
              newDestSortOrder[positionCounter++] = id;
            });
            // Add the new card at the end
            newDestSortOrder[positionCounter] = itemId;
            insertedCard = true;
          } else {
            // Insert the card at the specified position
            for (let i = 0; i <= destSortOrderEntries.length; i++) {
              if (i === destination.toPosition && !insertedCard) {
                // Insert the moved card
                newDestSortOrder[positionCounter++] = itemId;
                insertedCard = true;
              }
              if (i < destSortOrderEntries.length) {
                // Add existing cards
                const id = destSortOrderEntries[i][1];
                if (id.toString() !== itemId.toString()) {
                  newDestSortOrder[positionCounter++] = id;
                }
              }
            }
          }

          newDestColumn.sort_order = newDestSortOrder;

          // Update the columns
          newColumnsForStatusField[source.fromColumnId] = newSourceColumn;
          newColumnsForStatusField[destination.toColumnId] = newDestColumn;

          // Update the state with tenant-specific data
          set({
            board: {
              ...board,
              [targetTenant]: {
                ...board[targetTenant],
                [currentEntity]: {
                  ...newEntityData,
                  items: newItems,
                  columns: {
                    ...newEntityData.columns,
                    [currentStatusField]: newColumnsForStatusField,
                  },
                },
              },
            },
          });
        } catch (error) {
          console.error("Error handling card move:", error);
        }
      },

      // Handle column reordering
      handleColumnMove: (
        _: KanbanColumn,
        source: ColumnSource,
        destination: ColumnDestination,
        entityName: string,
        tenantId?: string,
      ) => {
        const { board, currentEntity, currentTenant } = get();
        const targetTenant = tenantId || currentTenant;
        const targetEntity = entityName || currentEntity;

        if (!targetTenant) {
          console.error("No tenant ID provided for column move");
          return;
        }

        if (!targetEntity || !board[targetTenant]?.[targetEntity]) {
          console.error("No board available for column move");
          return;
        }

        const entityData = board[targetTenant][targetEntity];
        const currentStatusField = entityData.currentStatusField;

        if (!currentStatusField) {
          console.error("No status field available for column move");
          return;
        }

        try {
          // Make a deep copy of entity data
          const newEntityData = { ...entityData };

          // Get all the columns for this status field
          const columnsForStatusField = {
            ...newEntityData.columns[currentStatusField],
          };
          if (Object.keys(columnsForStatusField).length === 0) {
            console.error(
              "No columns found for status field:",
              currentStatusField,
            );
            return;
          }

          // Get all columns sorted by position
          const columnEntries = Object.entries(columnsForStatusField).sort(
            ([, a], [, b]) => a.position - b.position,
          );

          // Get the column ID that's being moved
          const movedColumnId = columnEntries[source.fromPosition][0];

          // Update positions based on the move
          const newColumnsForStatusField = { ...columnsForStatusField };

          // Create a new order of column IDs
          const columnIds = columnEntries.map(([id]) => id);

          // Move the column ID
          columnIds.splice(source.fromPosition, 1);
          columnIds.splice(destination.toPosition, 0, movedColumnId);

          // Update positions for all columns
          columnIds.forEach((id, index) => {
            if (newColumnsForStatusField[id]) {
              newColumnsForStatusField[id] = {
                ...newColumnsForStatusField[id],
                position: index,
              };
            }
          });

          // Update the entity data with the new column positions
          set({
            board: {
              ...board,
              [targetTenant]: {
                ...board[targetTenant],
                [targetEntity]: {
                  ...newEntityData,
                  columns: {
                    ...newEntityData.columns,
                    [currentStatusField]: newColumnsForStatusField,
                  },
                },
              },
            },
          });
        } catch (error) {
          console.error("Error handling column move:", error);
        }
      },
    }),
    {
      name: "kanban-store",
      partialize: (state) => ({
        board: state.board,
        currentTenant: state.currentTenant,
      }),
    },
  ),
);

export default useKanbanStore;
